import { useState, useEffect } from "react";
import { GoogleMapSecretKey } from "../assets/GlobalVariables";

// Global state to track script loading
let scriptLoadingPromise = null;
let scriptLoaded = false;
let scriptLoadError = null;
let reloadAttempts = 0; // Track reload attempts to prevent infinite recursion
const MAX_RELOAD_ATTEMPTS = 2;

/**
 * Centralized Google Maps API loader with caching
 * Ensures the script is loaded only once per page, preventing duplicate loads
 * and reducing API charges
 */
// Track requested libraries globally to ensure we load with all needed libraries
let requestedLibraries = new Set();

export const loadGoogleMapsScript = (options = {}) => {
  const {
    libraries = [],
    language = localStorage.getItem("i18nextLng") || "he",
    callback = null,
  } = options;

  // Add requested libraries to the global set
  libraries.forEach((lib) => requestedLibraries.add(lib));

  // If already loaded, check if required libraries are available
  if (scriptLoaded && window.google && window.google.maps) {
    // Check if all requested libraries are loaded
    const allLibrariesLoaded = libraries.every(lib => {
      if (lib === 'places') {
        // Check for places library availability (new API uses importLibrary)
        return window.google.maps.places || (window.google.maps.importLibrary && typeof window.google.maps.importLibrary === 'function');
      }
      // Add checks for other libraries if needed
      return true;
    });

    if (allLibrariesLoaded) {
      if (callback) {
        callback();
      }
      return Promise.resolve();
    }
  }

  // If there's already a loading promise, wait for it and verify libraries are loaded
  if (scriptLoadingPromise) {
    return scriptLoadingPromise.then(() => {
      // Verify that all requested libraries are actually available
      if (libraries.length > 0 && window.google && window.google.maps) {
        const allLibrariesLoaded = libraries.every(lib => {
          if (lib === 'places') {
            // Check for places library availability (new API uses importLibrary)
            return window.google.maps.places || (window.google.maps.importLibrary && typeof window.google.maps.importLibrary === 'function');
          }
          return true;
        });
        
        if (!allLibrariesLoaded) {
          // Libraries not available, need to reload script
          console.warn("Script loaded but required libraries not available. Reloading with libraries:", libraries);
          scriptLoadingPromise = null;
          scriptLoaded = false;
          // Recursively call to reload with libraries
          return loadGoogleMapsScript({ libraries, language, callback });
        }
      }
      
      if (callback) {
        callback();
      }
    }).catch((error) => {
      // If loading failed, try again
      console.warn("Script loading failed, retrying with libraries:", libraries);
      scriptLoadingPromise = null;
      scriptLoaded = false;
      return loadGoogleMapsScript({ libraries, language, callback });
    });
  }

  // Check if script already exists in DOM
  const existingScript = document.querySelector(
    'script[src*="maps.googleapis.com/maps/api/js"]'
  );

  if (existingScript) {
    // Check if the existing script has the required libraries in its URL
    const existingScriptSrc = existingScript.src;
    let hasRequiredLibraries = true;
    
    if (libraries.length > 0) {
      // Check if all required libraries are in the URL
      hasRequiredLibraries = libraries.every(lib => {
        // Check various patterns for libraries parameter
        const hasLibrary = existingScriptSrc.includes(`libraries=${lib}`) || 
                          existingScriptSrc.includes(`libraries=${lib},`) ||
                          existingScriptSrc.includes(`,${lib}&`) ||
                          existingScriptSrc.includes(`,${lib}`);
        return hasLibrary;
      });
    }

    // Also check if libraries are actually available (even if URL says they should be)
    let librariesActuallyAvailable = true;
    if (libraries.length > 0 && window.google && window.google.maps) {
      librariesActuallyAvailable = libraries.every(lib => {
        if (lib === 'places') {
          // Check for places library availability (new API uses importLibrary)
          return window.google.maps.places || (window.google.maps.importLibrary && typeof window.google.maps.importLibrary === 'function');
        }
        return true;
      });
    }
    
    // If the existing script doesn't have the required libraries in URL OR libraries aren't actually available, remove it and load a new one
    if (!hasRequiredLibraries || !librariesActuallyAvailable) {
      const reason = !hasRequiredLibraries 
        ? "doesn't have required libraries in URL" 
        : "has libraries in URL but they're not actually loaded";
      console.warn(`Existing Google Maps script ${reason}:`, libraries, ". Removing and loading new script.");
      existingScript.remove();
      // Reset state to allow new script loading
      scriptLoaded = false;
      scriptLoadingPromise = null;
      reloadAttempts = 0; // Reset reload attempts when removing script
      // Continue to the new script loading logic below
    } else {
      // Script exists and should have the required libraries, check if it's loaded
      scriptLoadingPromise = new Promise((resolve, reject) => {
        let attempts = 0;
        const maxAttempts = 100; // Maximum 10 seconds (100 * 100ms)
        
        const checkLoaded = () => {
          attempts++;
          
          if (window.google && window.google.maps) {
            // Check if all requested libraries are actually loaded
            const allLibrariesLoaded = libraries.every(lib => {
              if (lib === 'places') {
                // Check for places library availability (new API uses importLibrary)
                return window.google.maps.places || (window.google.maps.importLibrary && typeof window.google.maps.importLibrary === 'function');
              }
              // Add checks for other libraries if needed
              return true;
            });

            if (allLibrariesLoaded) {
              scriptLoaded = true;
              scriptLoadError = null;
              reloadAttempts = 0; // Reset reload attempts on success
              resolve();
            } else if (attempts < maxAttempts) {
              // Libraries not ready yet, wait a bit and check again
              setTimeout(checkLoaded, 100);
            } else {
              // Max attempts reached - the script URL says it has libraries but they're not loading
              // This might mean the script was loaded without libraries or there's an API issue
              console.warn("Existing script loaded but required libraries not available after waiting. Removing and reloading with libraries:", libraries);
              
              // Prevent infinite recursion - check before incrementing
              if (reloadAttempts >= MAX_RELOAD_ATTEMPTS) {
                console.error("Max reload attempts reached. Places library may not be available.");
                scriptLoadError = new Error("Places library failed to load after reload attempts");
                reject(scriptLoadError);
              } else {
                existingScript.remove();
                scriptLoaded = false;
                scriptLoadingPromise = null;
                reloadAttempts++; // Increment before recursive call
                
                // Recursively call to reload with libraries
                loadGoogleMapsScript({ libraries, language, callback })
                  .then(() => {
                    reloadAttempts = 0; // Reset on success
                    resolve();
                  })
                  .catch((error) => {
                    reject(error);
                  });
              }
            }
          } else if (attempts < maxAttempts) {
            // Wait a bit and check again
            setTimeout(checkLoaded, 100);
          } else {
            // Max attempts reached - Google Maps API not loading
            console.error("Google Maps API failed to load after maximum attempts (existing script)");
            scriptLoadError = new Error("Google Maps API failed to load");
            reject(scriptLoadError);
          }
        };
        checkLoaded();
      });

      return scriptLoadingPromise.then(() => {
        // Double-check libraries are available after promise resolves
        if (libraries.length > 0 && window.google && window.google.maps) {
          const allLibrariesLoaded = libraries.every(lib => {
            if (lib === 'places') {
              // Check for places library availability (new API uses importLibrary)
              return window.google.maps.places || (window.google.maps.importLibrary && typeof window.google.maps.importLibrary === 'function');
            }
            return true;
          });
          
          if (!allLibrariesLoaded) {
            // Libraries still not available, need to load new script
            // Reset state and continue to load new script
            scriptLoadingPromise = null;
            scriptLoaded = false;
            
            // Prevent infinite recursion
            if (reloadAttempts >= MAX_RELOAD_ATTEMPTS) {
              console.error("Max reload attempts reached. Places library may not be available.");
              scriptLoadError = new Error("Places library failed to load after reload attempts");
              if (callback) {
                callback();
              }
              return Promise.reject(scriptLoadError);
            }
            
            reloadAttempts++;
            // Recursively call loadGoogleMapsScript to load new script with libraries
            return loadGoogleMapsScript({ libraries, language, callback }).finally(() => {
              reloadAttempts = 0; // Reset on success or failure
            });
          } else {
            // All libraries are loaded, call callback and return
            if (callback) {
              callback();
            }
            return Promise.resolve();
          }
        } else {
          if (callback) {
            callback();
          }
          return Promise.resolve();
        }
      }).catch((error) => {
        if (callback) {
          // Still call callback even on error for backward compatibility
          callback();
        }
        throw error;
      });
    }
  }

  // Create new script loading promise
  scriptLoadingPromise = new Promise((resolve, reject) => {
    // Build the script URL with all requested libraries
    const allLibraries = Array.from(requestedLibraries);
    const librariesParam = allLibraries.length > 0 ? `&libraries=${allLibraries.join(",")}` : "";
    const languageParam = language ? `&language=${language}` : "";
    const scriptUrl = `https://maps.googleapis.com/maps/api/js?key=${GoogleMapSecretKey}${librariesParam}${languageParam}`;

    // Create script element
    const script = document.createElement("script");
    script.src = scriptUrl;
    script.async = true;
    script.defer = true;
    script.id = "google-maps-api-script";

    // Set up event handlers
    script.onload = () => {
      // Wait for all requested libraries to be available
      let attempts = 0;
      const maxAttempts = 100; // Maximum 10 seconds (100 * 100ms)
      
      const checkLibrariesLoaded = () => {
        attempts++;
        
        if (window.google && window.google.maps) {
          // Use the libraries parameter passed to the function
          const allLibrariesLoaded = libraries.every(lib => {
            if (lib === 'places') {
              // Check for places library availability (new API uses importLibrary)
              return window.google.maps.places || (window.google.maps.importLibrary && typeof window.google.maps.importLibrary === 'function');
            }
            // Add checks for other libraries if needed
            return true;
          });

          if (allLibrariesLoaded) {
            scriptLoaded = true;
            scriptLoadError = null;
            reloadAttempts = 0; // Reset reload attempts on success
            resolve();
            if (callback) {
              callback();
            }
          } else if (attempts < maxAttempts) {
            // Libraries not ready yet, check again
            setTimeout(checkLibrariesLoaded, 100);
          } else {
            // Max attempts reached
            console.error("Google Maps Places library failed to load after maximum attempts");
            scriptLoadError = new Error("Places library failed to load");
            reject(scriptLoadError);
          }
        } else if (attempts < maxAttempts) {
          // Google Maps not ready yet, check again
          setTimeout(checkLibrariesLoaded, 100);
        } else {
          // Max attempts reached
          console.error("Google Maps API failed to load after maximum attempts");
          scriptLoadError = new Error("Google Maps API failed to load");
          reject(scriptLoadError);
        }
      };

      checkLibrariesLoaded();
    };

    script.onerror = (error) => {
      scriptLoadError = error;
      scriptLoadingPromise = null; // Reset so we can retry
      reject(error);
    };

    // Append to head
    document.head.appendChild(script);
  });

  return scriptLoadingPromise;
};

/**
 * Check if Google Maps API is already loaded
 */
export const isGoogleMapsLoaded = () => {
  return scriptLoaded && window.google && window.google.maps;
};

/**
 * Get the current loading state
 */
export const getGoogleMapsLoadingState = () => {
  return {
    loaded: scriptLoaded,
    loading: scriptLoadingPromise !== null,
    error: scriptLoadError,
  };
};

const LEGACY_NEARBY_DISABLED_KEY = "HR_GOOGLE_PLACES_LEGACY_NEARBY_DISABLED";
let legacyNearbySearchRuntimeDisabled = false;

function readLegacyNearbyDisabledFromStorage() {
  try {
    return sessionStorage.getItem(LEGACY_NEARBY_DISABLED_KEY) === "1";
  } catch {
    return false;
  }
}

/** Persistently skip legacy PlacesService.nearbySearch after fatal API rejection. */
export function markGooglePlacesLegacyNearbySearchDisabled() {
  legacyNearbySearchRuntimeDisabled = true;
  try {
    sessionStorage.setItem(LEGACY_NEARBY_DISABLED_KEY, "1");
  } catch {
    /* ignore */
  }
}

function isLegacyNearbyHardDisabled() {
  try {
    if (
      typeof import.meta !== "undefined" &&
      import.meta.env?.VITE_DISABLE_GOOGLE_PLACES_LEGACY_NEARBY === "true"
    ) {
      return true;
    }
  } catch {
    /* ignore */
  }
  if (legacyNearbySearchRuntimeDisabled) return true;
  if (readLegacyNearbyDisabledFromStorage()) {
    legacyNearbySearchRuntimeDisabled = true;
    return true;
  }
  return false;
}

/**
 * True when legacy PlacesService nearbySearch may be attempted.
 * Disabled via session flag after REQUEST_DENIED / legacy-not-activated failures.
 */
export function isGooglePlacesLegacyNearbySearchEnabled() {
  if (isLegacyNearbyHardDisabled()) return false;
  if (typeof window === "undefined") return false;
  return Boolean(window.google?.maps?.places?.PlacesService);
}

function isLegacyPlacesNearbyFailureStatus(status) {
  const S = window.google?.maps?.places?.PlacesServiceStatus;
  if (!S || status == null) return false;
  return (
    status === S.REQUEST_DENIED ||
    status === S.INVALID_REQUEST ||
    status === S.UNKNOWN_ERROR
  );
}

const DEFAULT_NEARBY_NAME_TYPES = [
  "tourist_attraction",
  "museum",
  "park",
  "restaurant",
  "subway_station",
];

/**
 * Safe legacy nearbySearch — returns { name, type } or [] without throwing/logging.
 * Reuses the existing Places nearbySearch path; no extra billing integration.
 */
export async function fetchGooglePlacesNearbyTyped({
  lat,
  lng,
  radius = 2200,
  types = DEFAULT_NEARBY_NAME_TYPES,
  language = localStorage.getItem("i18nextLng") || "he",
  limit = 24,
} = {}) {
  const latNum = Number(lat);
  const lngNum = Number(lng);
  const typeList = Array.isArray(types) && types.length ? types : DEFAULT_NEARBY_NAME_TYPES;
  const cap = Number.isFinite(Number(limit)) && Number(limit) > 0 ? Number(limit) : 24;
  if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return [];
  if (isLegacyNearbyHardDisabled()) return [];

  try {
    await loadGoogleMapsScript({ language, libraries: ["places"] });
  } catch {
    markGooglePlacesLegacyNearbySearchDisabled();
    return [];
  }

  if (!isGooglePlacesLegacyNearbySearchEnabled()) return [];

  try {
    const center = new window.google.maps.LatLng(latNum, lngNum);
    let serviceMap;
    let service;
    try {
      serviceMap = new window.google.maps.Map(document.createElement("div"), {
        center,
        zoom: 14,
      });
      service = new window.google.maps.places.PlacesService(serviceMap);
    } catch {
      markGooglePlacesLegacyNearbySearchDisabled();
      return [];
    }

    const fetchOneType = (type) =>
      new Promise((resolve) => {
        let settled = false;
        const finish = (value) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          resolve(value);
        };
        const timer = setTimeout(() => finish([]), 4000);
        try {
          service.nearbySearch(
            { location: center, radius, type },
            (results, status) => {
              try {
                if (
                  status === window.google.maps.places.PlacesServiceStatus.OK &&
                  Array.isArray(results)
                ) {
                  finish(
                    results
                      .map((r) => ({
                        name: String(r?.name || "").trim(),
                        type,
                      }))
                      .filter((item) => item.name)
                  );
                  return;
                }
                if (isLegacyPlacesNearbyFailureStatus(status)) {
                  markGooglePlacesLegacyNearbySearchDisabled();
                }
              } catch {
                markGooglePlacesLegacyNearbySearchDisabled();
              }
              finish([]);
            }
          );
        } catch {
          markGooglePlacesLegacyNearbySearchDisabled();
          finish([]);
        }
      });

    const batches = await Promise.all(typeList.map((type) => fetchOneType(type)));
    if (!isGooglePlacesLegacyNearbySearchEnabled()) return [];

    const merged = [];
    const seen = new Set();
    for (const list of batches) {
      for (const item of list) {
        const key = item.name.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        merged.push(item);
        if (merged.length >= cap) return merged;
      }
    }
    return merged;
  } catch {
    markGooglePlacesLegacyNearbySearchDisabled();
    return [];
  }
}

/**
 * Safe legacy nearbySearch — returns place names or [] without throwing/logging.
 * Falls back silently when Places legacy API is unavailable (e.g. LegacyApiNotActivatedMapError).
 */
export async function fetchGooglePlacesNearbyNames({
  lat,
  lng,
  radius = 2200,
  types = DEFAULT_NEARBY_NAME_TYPES,
  language = localStorage.getItem("i18nextLng") || "he",
} = {}) {
  const typed = await fetchGooglePlacesNearbyTyped({
    lat,
    lng,
    radius,
    types,
    language,
    limit: 20,
  });
  return typed.map((item) => item.name);
}

/**
 * Reset the loader state (useful for testing or error recovery)
 */
export const resetGoogleMapsLoader = () => {
  scriptLoadingPromise = null;
  scriptLoaded = false;
  scriptLoadError = null;
  reloadAttempts = 0;
};

/**
 * React hook that mimics useLoadScript from @react-google-maps/api
 * but uses our centralized loader to prevent duplicate script loading
 */
export const useGoogleMapsScript = (options = {}) => {
  const {
    googleMapsApiKey,
    libraries = [],
    language = localStorage.getItem("i18nextLng") || "he",
  } = options;

  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    // Check if already loaded
    if (window.google && window.google.maps) {
      setIsLoaded(true);
      setLoadError(null);
      return;
    }

    // Load using centralized loader
    loadGoogleMapsScript({
      libraries,
      language,
    })
      .then(() => {
        setIsLoaded(true);
        setLoadError(null);
      })
      .catch((error) => {
        setLoadError(error);
        setIsLoaded(false);
      });
  }, [libraries.join(","), language]);

  return { isLoaded, loadError };
};

